import { FunctionUtils } from "Global/Base/Utils/FunctionUtils";
import { Event } from "../../../../Global/Base/Events";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "../../../../Global/Base/Rights/RightTypes";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { GenericControl } from "../Controls/Control";
import { GenericControlParams } from "../Controls/ControlInterfaces";
import { GenericControlCollection } from "../Controls/GenericControlCollection";
import { UIManager } from "../UIManager";
import { ToolbarGroup } from "./ToolbarGroup";

/**
 * Skupiny obsazene v jedne zalozce, kontejner pro skupiny
 */
export class ToolbarGroups extends GenericControlCollection { //todo: MV - zrusit a funkcionalitu presunout na ToolbarPage
    //#region Fields
    private _visible: boolean = false;
    private _containerResizeHandler: () => void;
    //#endregion Fields

    //#region  Events
    public visibilityChange: Event<ToolbarGroups, boolean> = new Event<ToolbarGroups, boolean>();
    //#endregion  Events

    //#region Properties
    /** Jednotlive skupiny */
    public groups: ToolbarGroup[] = [] as Array<ToolbarGroup>;
    //#endregion Properties

    //#region Methods
    constructor(params: GenericControlParams) {
        super(params);
        //Naslouchání resize elementu ActionsContainer/ToolbarGroups
        this._containerResizeHandler = () => this.throttledResizeGroups();
        UIManager.instance.resizeObserve(this.element, this._containerResizeHandler);
    }

    /**
     * Pridani nove skupiny
     * @param lockey - lokalizacni klic
     * @param toMenu - menu do ktereho skupina patri
     * @param ctrlID - identifikator
     * @param priority - pozice na kterou se ma novy element vlozit
     */
    private _addGroup(lockey?: string, toMenu: MenuType = MenuType.mtNone, ctrlID?: string, priority?: number): ToolbarGroup {
        const div = document.createElement("div");

        let position = this.groups.length;
        if (priority !== undefined) {
            for (let i = 0; i < this.groups.length; i++) {
                if (this.groups[i].priority > priority) {
                    position = i;
                    break;
                }
            }
        }

        if (position < this.groups.length)
            this.element.insertBefore(div, this.element.childNodes[position]);
        else
            this.element.appendChild(div);

        const newGroup = new ToolbarGroup({
            element: div,
            originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION,
            lockey,
            toMenu,
            priority,
            position,
            resizeGroups: this.throttledResizeGroups,
        });

        if (position < this.groups.length)
            this.groups.splice(position, 0, newGroup);
        else
            this.groups.push(newGroup);

        newGroup.ctrlID = ctrlID;
        //newGroup.ToMenu = toMenu;

        this._events.add(newGroup.visibleChange, (_sender, _e) => this._checkChildrenAndSetVisibility());
        this._checkChildrenAndSetVisibility();

        return newGroup;
    }

    public set visible(value: boolean) {
        if (value != this._visible) {
            this._visible = value;
            this.visibilityChange.trigger(this, value);
        }
    }

    public get visible(): boolean {
        return this._visible;
    }

    /**
     * Pokud jsou vsechny podrizene skupiny neviditelne nebo zadnou skupinu nema tak nastavim nadrizenou page jako neviditelnou
     */
    private _checkChildrenAndSetVisibility(): void {
        let newVisible = false;
        for (let i = 0; i < this.groups.length; i++) {
            if (this.groups[i].visible) {
                newVisible = true;
                break;
            }
        }

        this.visible = newVisible;
    }

    /**
     * Provede upravu toolbaru a polozek na nem.
     *
     * Pokud vrati false element jeste neni pridan do DOM a nelze tedy zjistit dostupnou sirku
     */
    public resizeGroups(): boolean {
        if (!document.contains(this.element) || this.element.clientWidth === 0) {
            return false;
        }
        //logger.log("ResizingGroups - %d", Date.now());
        const visibleGrp: Array<ToolbarGroup> = [];
        let staticGroupWidth = 0;
        // najde viditelne skupiny a nastavi jim "neomezenou" maximalni sirku, pro zjisteni skutecne potrebne sirky groupy
        for (const grp of this.groups) {
            if (!grp.visible)
                continue;

            grp.moveAllActionsToGroup(); // Na začátku strčíme všechny akce do grupy, aby se načetla jejich velikost (nenulová clientWidth)

            if (!grp.dynamic) {
                staticGroupWidth += grp.element.clientWidth;
                continue;
            }

            visibleGrp.push(grp);
        }

        // nastavi se pomocne promenne a vypocita se prumerna maximalni sirka na jeden toolbar
        const groupCnt = visibleGrp.length;

        if (groupCnt == 0)
            return false;
        const compStyle = getComputedStyle(this.element);
        const paddingOffset = parseInt(compStyle.paddingLeft) + parseInt(compStyle.paddingRight);
        const fullWidth = this.element.parentElement.clientWidth - staticGroupWidth - paddingOffset;
        const oneWidthOffset = 25;
        const maxOneWidth = fullWidth / groupCnt;
        const realWidth: number[] = [];
        let widthBank = 0;

        if (isNaN(paddingOffset))
            return false;

        // provede se vypocet prebivajici nebo naopak chybejici sirky
        for (const grp of visibleGrp) {
            const w = grp.element.clientWidth;
            realWidth.push(w);
            if (w < maxOneWidth)
                widthBank += maxOneWidth - w;
        }

        // pokud nejaka sirka v banku navic je napriklad diky groupam, ktere nevyuzivaji plne svou maximalni sirku, je to pouzito pro skupiny, ktere potrebuji vice prostoru
        for (let i = 0; i < groupCnt; i++) {
            const rw = realWidth[i] + oneWidthOffset;
            const grp = visibleGrp[i];

            grp.maxWidth = maxOneWidth;
            if (rw > maxOneWidth) {
                const needWidth = rw - maxOneWidth; // kolik sirky potrebuje panel pro cele zobrazeni
                // vypocet sirky podle toho kolik volne sirky viditelne je dostupne
                if (widthBank > 0) {
                    if (needWidth > widthBank) {
                        grp.maxWidth = maxOneWidth + widthBank;
                        widthBank = 0;
                    } else {
                        grp.maxWidth = maxOneWidth + needWidth;
                        widthBank -= needWidth;
                    }
                }
            } else {
                grp.maxWidth = rw;
            }
            grp.checkIfActionsFitGroup();
        }

        return true;
    }

    public throttledResizeGroups = FunctionUtils.throttleFunctionBounded(() => this.resizeGroups(), 500);

    /**
     * Vyhleda skupinu podle lockey nebo vytvori a prida novou
     */
    public addOrFindGroup(lockey: string, toMenu: MenuType, ctrlID: string, priority?: number): ToolbarGroup {
        let group: ToolbarGroup = this._findGroup(ctrlID);
        if (group != null)
            return group;

        if (!ctrlID) {
            group = this._findGroupByToMenu(toMenu);
            if (group != null)
                return group;
        }

        return this._addGroup(lockey, toMenu, ctrlID, priority);
    }

    /**Najde konkretni skupinu*/
    private _findGroup(ctrlID: string): ToolbarGroup {
        for (const group of this.groups) {
            if (group.ctrlID == ctrlID) {
                return group;
            }
        }

        return null;
    }

    private _findGroupByToMenu(toMenu: MenuType): ToolbarGroup {
        for (const group of this.groups) {
            if (group.toMenu == toMenu) {
                return group;
            }
        }

        return null;
    }

    /**Vyhleda masternode*/
    public findMasterNode(ctrlID: string): GenericControl[] {
        let group: ToolbarGroup = this._findGroup(ctrlID);

        let controls: GenericControl[] = [];

        if (group != null) {
            controls.push(group);
            return controls;
        }

        for (let i: number = 0; i < this.groups.length; i++) {
            group = this.groups[i];
            //TODO: ukoncovaci podminka, aby neprochazel vsechny elementy
            controls = controls.concat(group.findMasterNode(ctrlID));
        }

        return controls;
    }

    /**
     * Vytvoreni defaultnich skupin
     */
    public createDefaultGroups(): void {
        this._addGroup("GUI-181666");
        this._addGroup();
        this._addGroup("GUI-491802");
    }

    public sortByPriority(): void {
        //this.Groups.forEach((group) => { group.Groups.SortByPriority(); })
    }
    //#endregion Methods

    //#region dispose
    public override dispose(): void {
        UIManager.instance.resizeUnobserve(this.element, this._containerResizeHandler);
        // Musí se volat již na začátku, kvůli odregistraci listeneru _checkChildrenAndSetVisibility.
        // Ten by totiž - v cyklu níže - mohl způsobit padavé volání na již disposnutých skupinách.
        super.dispose();

        const len = this.groups.length;
        for (let i = 0; i < len; i++)
            if (this.groups[i])
                this.groups[i].dispose();

        this.groups.length = 0;
    }
    //#endregion dispose
}
